import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Input,
  Label,
  FormGroup,
  Table,
  Badge,
  Button,
  Spinner,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { CSVLink } from "react-csv";
import {
  getRoomHistory,
  getRoomHistoryByDateRange,
} from "../../helpers/api.ts";
import { toast, ToastContainer } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import ReservationStatusBadge from "../../customcomponents/ReservationStatusBadge";

const RoomHistory = () => {
  const { roomId, roomNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roomHistory, setRoomHistory] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalEntries: 0,
    pageSize: 10,
  });
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    console.log("Room ID:", roomId);
    console.log("Room Number:", roomNumber);
    if (roomId) {
      fetchRoomHistory();
    }
  }, [roomId, pagination.currentPage]);

  const fetchRoomHistory = async (startDate = null, endDate = null) => {
    setLoading(true);
    try {
      console.log("Fetching room history for room ID:", roomId);
      console.log("Date range:", { startDate, endDate });
      console.log("Page:", pagination.currentPage);

      let response;
      if (startDate && endDate) {
        response = await getRoomHistoryByDateRange(
          roomId,
          startDate,
          endDate,
          pagination.currentPage,
          pagination.pageSize
        );
      } else {
        response = await getRoomHistory(
          roomId,
          pagination.currentPage,
          pagination.pageSize
        );
      }

      console.log("API Response:", response);

      if (response?.success) {
        setRoomHistory(response.data.history || []);
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalEntries: response.data.totalEntries,
          pageSize: pagination.pageSize,
        });

        if (response.data.history?.length === 0) {
          toast.info("No history records found for this room");
        }
      } else {
        toast.error(response?.message || "Failed to fetch room history");
      }
    } catch (error) {
      console.error("Error fetching room history:", error);
      toast.error(
        "Failed to fetch room history: " + (error.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateRangeSubmit = () => {
    if (dateRange.startDate && dateRange.endDate) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      fetchRoomHistory(dateRange.startDate, dateRange.endDate);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "AVAILABLE":
        return "success";
      case "OCCUPIED":
        return "warning";
      case "UNDER MAINTENANCE":
        return "danger";
      default:
        return "secondary";
    }
  };

  const csvData = roomHistory.map((history) => {
    const menuDetails = history.ordered_menu?.length
      ? history.ordered_menu.map((item) =>
          item.menu?.dish
            ? `${item.menu.dish} (Qty: ${item.quantity}, Cost: ₹${
                item.menu.cost
              }, Total: ₹${item.quantity * (item.menu.cost || 0)})`
            : "Unknown Dish"
        )
      : ["No Menu Items Ordered"];

    const totalMenuCost = history.ordered_menu?.length
      ? history.ordered_menu.reduce((total, item) => {
          return total + (item.menu?.cost || 0) * item.quantity;
        }, 0)
      : 0;

    return {
      Date: new Date(history.changeTimestamp).toLocaleDateString(),
      "Room Number": history.room_number,
      "Room Type": history.room_type,
      "AC/Non-AC": history.ac_no_ac,
      Status: history.roomstatus,
      Tariff: history.tariff,
      "Guest Name": history.guest_info?.guest_name || "-",
      "Guest Email": history.guest_info?.email || "-",
      "Guest Phone": history.guest_info?.mobile_number || "-",
      "Check In": history.reservation_details?.check_in_date
        ? new Date(
            history.reservation_details.check_in_date
          ).toLocaleDateString()
        : "-",
      "Check Out": history.reservation_details?.check_out_date
        ? new Date(
            history.reservation_details.check_out_date
          ).toLocaleDateString()
        : "-",
      "Reservation Status": history.reservation_details?.status || "-",
      "Changed By": history.changed_by?.name || "-",
      "Staff Role": history.changed_by?.role || "-",
      Hotel: history.hotel_name || "-",
      "Menu Items": menuDetails.join(", "),
      "Total Menu Cost": `₹${totalMenuCost}`,
    };
  });

  return (
    <div className="page-content">
      {/* <h4 className="card-title">Room History</h4> */}
      <Container fluid>
        <Breadcrumbs
          title="Room History"
          breadcrumbItem={`Room ${roomNumber} History`}
        />

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-column flex-md-row gap-2 mb-4">
              <h4 className="card-title mb-0">Room History</h4>
              <div className="d-flex gap-2 w-100 w-md-auto">
                <Button color="secondary" onClick={() => navigate("/rooms")} className="w-100 w-md-auto">
                  Back to Rooms
                </Button>
                {roomHistory.length > 0 && (
                  <CSVLink
                    data={csvData}
                    filename={`room-${roomNumber}-history.csv`}
                    className="btn btn-success w-100 w-md-auto"
                  >
                    Download CSV
                  </CSVLink>
                )}
              </div>
            </div>

            <div className="mb-4">
              <Row className="g-2 d-flex align-items-center">
                <Col xs={12} md={4}>
                  <FormGroup>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      name="startDate"
                      value={dateRange.startDate}
                      onChange={handleDateRangeChange}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={4}>
                  <FormGroup>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      name="endDate"
                      value={dateRange.endDate}
                      onChange={handleDateRangeChange}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={4} className="d-flex align-items-end">
                  <Button
                    color="primary"
                    onClick={handleDateRangeSubmit}
                    disabled={!dateRange.startDate || !dateRange.endDate}
                    className="w-100 w-md-auto mt-2"
                  >
                    Filter by Date
                  </Button>
                </Col>
              </Row>
            </div>

            {loading ? (
              <div className="text-center">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                <Table className="table-centered table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Room Details</th>
                      <th>Guest Details</th>
                      <th>Check In/Out</th>
                      <th>Reservation Status</th>
                      {/* <th>Changed By</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {roomHistory.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No history found
                        </td>
                      </tr>
                    ) : (
                      roomHistory.map((history, index) => (
                        <tr key={history.history_id || index}>
                          <td>
                            {new moment(
                              history.changeTimestamp
                            ).format("DD-MM-YYYY")}
                          </td>
                          <td>
                            <Badge
                              color={getStatusBadgeColor(history.roomstatus)}
                            >
                              {history.roomstatus}
                            </Badge>
                          </td>
                          <td>
                            <div>
                              <strong>{history.room_number}</strong>
                            </div>
                            <div>
                              {history.room_type} - {history.ac_no_ac}
                            </div>
                            <small className="text-muted">
                              ₹{history.tariff}
                            </small>
                          </td>
                          <td>
                            {history.guest_info ? (
                              <>
                                <div>
                                  <strong>
                                    {history.guest_info.guest_name}
                                  </strong>
                                </div>
                                <div>{history.guest_info.email}</div>
                                <small>
                                  {history.guest_info.mobile_number}
                                </small>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {history.reservation_details ? (
                              <div>
                                <div>
                                  <strong>Check In:</strong>
                                  <br />
                                  {new moment(history.reservation_details.check_in_date).format("DD-MM-YYYY")}
                                </div>
                                <div className="mt-2">
                                  <strong>Check Out:</strong>
                                  <br />
                                  {new moment(history.reservation_details.check_out_date).format("DD-MM-YYYY")}
                                </div>
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {history.reservation_details ? (
                              <>
                                <ReservationStatusBadge status={history.reservation_details.status} />
                                <div className="mt-2">
                                  <Button
                                    color="link"
                                    className="p-0"
                                    onClick={() =>
                                      navigate(
                                        `/reservation/${history.reservation_id}`
                                      )
                                    }
                                  >
                                    View Details
                                  </Button>
                                </div>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                          {/* <td>
                            <div>{history.changed_by?.name || '-'}</div>
                            <small className="text-muted">{history.changed_by?.role || '-'}</small>
                          </td> */}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
                </div>

                {roomHistory.length > 0 && (
                  <div className="d-flex justify-content-end mt-3">
                    <Pagination>
                      <PaginationItem disabled={pagination.currentPage === 1}>
                        <PaginationLink
                          previous
                          onClick={() =>
                            handlePageChange(pagination.currentPage - 1)
                          }
                        />
                      </PaginationItem>

                      {[...Array(pagination.totalPages)].map((_, i) => (
                        <PaginationItem
                          key={i}
                          active={i + 1 === pagination.currentPage}
                        >
                          <PaginationLink
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem
                        disabled={
                          pagination.currentPage === pagination.totalPages
                        }
                      >
                        <PaginationLink
                          next
                          onClick={() =>
                            handlePageChange(pagination.currentPage + 1)
                          }
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default RoomHistory;
